<template>
  <div class="hyper-params-container">
    <field-container v-for="index in filteredHyperParamIndexies" :label="hyperParamSummaries[index].hyperParamName"
                     :tip="hyperParamSummaries[index].description.text">

      <field-input v-if="isType(hyperParams[index].hyperParamId, 'input')" :key="hyperParams[index].hyperParamId"
                   v-model="hyperParams[index].value" :ref="hyperParams[index].hyperParamId | camelize"
                   :label="hyperParamSummaries[index].hyperParamName" :name="hyperParamSummaries[index].hyperParamId"
                   :placeholder="hyperParamSummaries[index].defaultVal"
                   :disabled="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].disabled"
                   :validate="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].validate"
                   :validateLabel="hyperParamSummaries[index].hyperParamName"></field-input>

      <field-radio-group v-else-if="isType(hyperParams[index].hyperParamId, 'radio-group')"
                         :key="hyperParams[index].hyperParamId"
                         v-model="hyperParams[index].value" :ref="hyperParams[index].hyperParamId | camelize"
                         :label="hyperParamSummaries[index].hyperParamName"
                         :name="hyperParamSummaries[index].hyperParamId"
                         :validate="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].validate"
                         :validateLabel="hyperParamSummaries[index].hyperParamName"
                         :options="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].options"
                         :readonly="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].readonly"></field-radio-group>

      <field-switch-input v-else-if="isType(hyperParams[index].hyperParamId, 'switch-input')"
                          :key="hyperParams[index].hyperParamId"
                          v-model="hyperParams[index].value" :ref="hyperParams[index].hyperParamId | camelize"
                          :label="hyperParamSummaries[index].hyperParamName"
                          :name="hyperParamSummaries[index].hyperParamId"
                          :placeholder="hyperParamSummaries[index].defaultVal"
                          :inactiveLabel="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].inactiveLabel"
                          :activeLabel="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].activeLabel"
                          :extraOptions="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].extraOptions"
                          :validate="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].validate"
                          :validateLabel="hyperParamSummaries[index].hyperParamName"></field-switch-input>

      <field-input-target v-if="isType(hyperParams[index].hyperParamId, 'input-target')"
                          :key="hyperParams[index].hyperParamId"
                          v-model="hyperParams[index].value" :ref="hyperParams[index].hyperParamId | camelize"
                          :label="hyperParamSummaries[index].hyperParamName"
                          :name="hyperParamSummaries[index].hyperParamId"
                          :placeholder="hyperParamSummaries[index].defaultVal"
                          :disabled="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].disabled"
                          :validate="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].validate"
                          :validateLabel="hyperParamSummaries[index].hyperParamName"
                          :targetValidateMethod="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].targetValidateMethod"
                          :targetValue.sync="findHyperParam(HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].target).value"></field-input-target>

      <stop-words v-else-if="isType(hyperParams[index].hyperParamId, 'stop-words')"
                  :key="hyperParams[index].hyperParamId"
                  v-model="hyperParams[index].value" :ref="hyperParams[index].hyperParamId | camelize"
                  :label="hyperParamSummaries[index].hyperParamName" :name="hyperParamSummaries[index].hyperParamId"
                  :placeholder="hyperParamSummaries[index].defaultVal"
                  :disabled="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].disabled"
                  :validate="HYPER_PARAM_SETTINGS[hyperParams[index].hyperParamId].validate"
                  :validateLabel="hyperParamSummaries[index].hyperParamName"></stop-words>

      <n-units v-else-if="isType(hyperParams[index].hyperParamId, 'n_units')" :key="hyperParams[index].hyperParamId"
               :hyperParamSummary="hyperParamSummaries[index]" :ref="hyperParams[index].hyperParamId | camelize"
               :nUnits1.sync="hyperParams[index].value" :nUnits2.sync="findHyperParam('n_units_2').value"
               :nUnits3.sync="findHyperParam('n_units_3').value"></n-units>
    </field-container>
  </div>
</template>

<script lang="js">
import StopWordsCmp from './hyper-param/stop-words.vue'
import NUnitsCmp from './hyper-param/n-units.vue'

import { HYPER_PARAM_SETTINGS } from '../../../defines/hyper-param'
import CommonUtil from '../../../utils/common'

export default {
  name: 'HyperParamsCmp',
  components: {
    'stop-words': StopWordsCmp,
    'n-units': NUnitsCmp
  },
  props: {
    label: '',
    categoryId: '',
    hyperParams: Array,
    hyperParamSummaries: Array
  },
  computed: {
    HYPER_PARAM_SETTINGS () {
      return HYPER_PARAM_SETTINGS
    },
    filteredHyperParamIndexies () {
      let result = []

      for (let index in this.hyperParams) {
        if (['n_units_2', 'n_units_3'].indexOf(this.hyperParams[index].hyperParamId) < 0) {
          result.push(Number(index))
        }
      }
      return result
    }
  },
  created () {
    for (const hyperParam of this.hyperParams) {
      if (CommonUtil.isNotNull(hyperParam.value)) {
        hyperParam.value = String(hyperParam.value)
      }
    }
  },
  mounted () {
    this.setEvents()
  },
  beforeUpdate () {
    this.clearEvents()
  },
  updated () {
    this.setEvents()
  },
  destroyed () {
    this.clearEvents()
  },
  methods: {
    setEvents () {
      if (CommonUtil.isNotEmpty(this.$refs.knAlgorithm)) {
        this.$refs.knAlgorithm[0].$on('change', this.onChangeKnAlgorithm)
        this.onChangeKnAlgorithm(this.$refs.knAlgorithm[0].$props['value'])
      }
      if (CommonUtil.isNotEmpty(this.$refs.kernel)) {
        this.$refs.kernel[0].$on('change', this.onChangeKernel)
        this.onChangeKernel(this.$refs.kernel[0].$props['value'])
      }
      if (CommonUtil.isNotEmpty(this.$refs.createRowFreq)) {
        this.$refs.createRowFreq[0].$on('change', this.onChangeCreateRowFreq)
        this.onChangeCreateRowFreq(this.$refs.createRowFreq[0].$props['value'])
      }
    },
    clearEvents () {
      if (CommonUtil.isNotEmpty(this.$refs.knAlgorithm)) {
        this.$refs.knAlgorithm[0].$off('change', this.onChangeKnAlgorithm)
      }
      if (CommonUtil.isNotEmpty(this.$refs.kernel)) {
        this.$refs.kernel[0].$off('change', this.onChangeKernel)
      }
    },
    onChangeKnAlgorithm (value) {
      if (CommonUtil.isNotEmpty(this.$refs.knLeafSize)) {
        this.$refs.knLeafSize[0].$set(this.$refs.knLeafSize[0], 'disabled', value === 'auto')
      }
    },
    onChangeKernel (value) {
      if (CommonUtil.isNotEmpty(this.$refs.svDegree)) {
        this.$refs.svDegree[0].$set(this.$refs.svDegree[0], 'disabled', value !== 'poly')
      }
      if (CommonUtil.isNotEmpty(this.$refs.svGamma)) {
        this.$refs.svGamma[0].$set(this.$refs.svGamma[0], 'disabled', value === 'linear')
      }
    },
    onChangeCreateRowFreq (value) {
      if (CommonUtil.isNotEmpty(this.$refs.frequencyLimit)) {
        this.$refs.frequencyLimit[0].$set(this.$refs.frequencyLimit[0], 'disabled', value === 0)
      }
      if (CommonUtil.isNotEmpty(this.$refs.ignoreTooFew)) {
        this.$refs.ignoreTooFew[0].$set(this.$refs.ignoreTooFew[0], 'disabled', value === 0)
      }
      if (CommonUtil.isNotEmpty(this.$refs.maxRemainsCount)) {
        this.$refs.maxRemainsCount[0].$set(this.$refs.maxRemainsCount[0], 'disabled', value === 0)
      }
    },
    isType (hyperParamId, type) {
      return (this.HYPER_PARAM_SETTINGS.hasOwnProperty(hyperParamId) &&
          this.HYPER_PARAM_SETTINGS[hyperParamId].type === type)
    },
    findHyperParam (hyperParamId) {
      for (let hyperParam of this.hyperParams) {
        if (hyperParam.hyperParamId === hyperParamId) {
          return hyperParam
        }
      }
      return null
    }
  }
}

</script>

<style scoped>
  .hyper-params-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
</style>



// WEBPACK FOOTER //
// src/components/form/field/hyper-params.vue